import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
// Can't be imported as named import, it breaks the app
// eslint-disable-next-line import/no-named-as-default
import { getCoord } from '@turf/invariant';
import difference from '@turf/difference';
import union from '@turf/union';
import * as helpers from '@turf/helpers';
import bboxPolygon from '@turf/bbox-polygon';
import { Feature, MultiPolygon, Polygon, Point } from '@turf/helpers';

import { getNearestPointOnPolygon } from './utils';

const getNearestSnapPointCoords = (
  point: Feature<Point>,
  polygon: Feature<Polygon | MultiPolygon>,
) => {
  const nearestSnapPoint = getNearestPointOnPolygon(polygon, point);

  return nearestSnapPoint && getCoord(nearestSnapPoint);
};

export const createWholeWorldPolygon = () => bboxPolygon([-180, -90, 180, 90]);

export enum OMIT_POLYGON_TYPES {
  allow = 'allow',
  restrict = 'restrict',
}

type PolygonMeta =
  | {
      type: OMIT_POLYGON_TYPES.allow;
      geometry: Feature<Polygon | MultiPolygon>;
    }
  | {
      type: OMIT_POLYGON_TYPES.restrict;
      geometry: Feature<Polygon | MultiPolygon> | null;
    };

export const constructAllowedPolygon = (
  polygons: PolygonMeta[] = [],
): Feature<Polygon | MultiPolygon> | null => {
  // combine polygons by type
  const combinedPolygonGeometriesByType = polygons.reduce(
    (acc, polygon) => {
      if (polygon.type == OMIT_POLYGON_TYPES.restrict && !polygon.geometry) {
        // some restriction polygons may be nullable, so just skip them
        return acc;
      }

      if (acc[polygon.type]) {
        acc[polygon.type] = union(acc[polygon.type]!, polygon.geometry!);
      } else {
        acc[polygon.type] = polygon.geometry;
      }

      return acc;
    },
    {} as Record<OMIT_POLYGON_TYPES, Feature<Polygon | MultiPolygon> | null>,
  );

  // create combined polygon from allow polygon or whole-world polygon
  const allowedPolygon: Feature<Polygon | MultiPolygon> =
    combinedPolygonGeometriesByType[OMIT_POLYGON_TYPES.allow]
      ? combinedPolygonGeometriesByType[OMIT_POLYGON_TYPES.allow]
      : createWholeWorldPolygon();

  const restrictedPolygon =
    combinedPolygonGeometriesByType[OMIT_POLYGON_TYPES.restrict];

  // create combined polygon from restrict polygon or calculate difference between allow and restrict polygon, null if there is no overlap
  if (restrictedPolygon) {
    return difference(allowedPolygon, restrictedPolygon);
  }

  return allowedPolygon;
};

/**
 * Creates a function to modify coordinates and omit polygons, polygon is a { geometry: {}, type: string }
 */
export const createOmitPolygons = (polygons) => {
  // not allow any coordinates change in case allow polygon is null
  if (
    polygons.some(
      (polygon) =>
        polygon.type === OMIT_POLYGON_TYPES.allow && !polygon.geometry,
    )
  ) {
    return () => null;
  }

  const allowedPolygon = constructAllowedPolygon(polygons);
  return (coords) => {
    // return no coords if no polygon is calculated (when allow and restrict polygons does not overlap)
    if (!allowedPolygon) {
      return null;
    }

    const point = helpers.point(coords);
    if (!booleanPointInPolygon(point, allowedPolygon)) {
      return getNearestSnapPointCoords(point, allowedPolygon);
    }

    return coords;
  };
};
